import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { AlertComponent, ButtonComponent } from '@libs/common/components';
import { MarkupComponent, QuantityCounterComponent, SelectComponent, TextComponent } from '@libs/form/components';
import { IconComponent } from '@libs/components/icons';
import {
  AttributesModel,
  AutocompleteOptionModel,
  MarkupItemModel,
  QuantityCounterInputModel,
  SelectInputModel,
  TextInputModel,
} from '@libs/form/models';
import { ShoppingListModel } from '@libs/shop/models';
import { distinctUntilChanged } from 'rxjs/operators';
import {
  ProductCounterData,
  ProductDetailData,
  ShoppingListInfoData,
  ShoppingListInputData,
  ShoppingListNameInputData,
  TitleInfoData,
} from './add-to-shopping-list.form.model';
import { AddToShoppingListFormResult, NewShoppingListId } from './add-to-shopping-list.model';

@Component({
  selector: 'ui-add-to-shopping-list',
  templateUrl: './add-to-shopping-list.component.html',
  styleUrls: ['./add-to-shopping-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TextComponent,
    ReactiveFormsModule,
    QuantityCounterComponent,
    MarkupComponent,
    MatButtonModule,
    AlertComponent,
    MatDialogModule,
    ButtonComponent,
    IconComponent,
    SelectComponent,
  ],
})
export class AddToShoppingListComponent implements OnInit, OnChanges {
  @Input() public sku?: string;
  @Input() public label?: string;
  @Input() public shoppingLists?: ShoppingListModel[];
  @Input() public defaultAmount?: number;
  @Input() public error: string | undefined;

  @Output() public submitResult: EventEmitter<AddToShoppingListFormResult> = new EventEmitter<AddToShoppingListFormResult>();
  @Output() public abortClicked: EventEmitter<void> = new EventEmitter<void>();
  @Output() public retryClicked: EventEmitter<void> = new EventEmitter<void>();
  @Output() public closeClicked: EventEmitter<void> = new EventEmitter<void>();

  public dataShoppingList: SelectInputModel = ShoppingListInputData;
  public dataQuantityCounter: QuantityCounterInputModel = ProductCounterData;
  public dataShoppingListName: TextInputModel = ShoppingListNameInputData;
  public dataProductDetail: MarkupItemModel = ProductDetailData;
  public dataShoppingListInfo: MarkupItemModel = ShoppingListInfoData;
  public dataTitle: MarkupItemModel = TitleInfoData;

  public quantityCounterControl: UntypedFormControl = new UntypedFormControl();
  public shoppingListControl: UntypedFormControl = new UntypedFormControl();
  public shoppingListNameControl: UntypedFormControl = new UntypedFormControl();

  public group: FormGroup = new UntypedFormGroup({
    [this.dataQuantityCounter.name]: this.quantityCounterControl,
    [this.dataShoppingList.name]: this.shoppingListControl,
    [this.dataShoppingListName.name]: this.shoppingListNameControl,
  });

  public constructor(private readonly changeDetector: ChangeDetectorRef) {
  }

  public ngOnInit(): void {
    this.shoppingListControl.valueChanges.pipe(distinctUntilChanged()).subscribe((value: string) => {
      this.setNewListNameInputState(value);
      this.shoppingListNameControl.reset();
      this.shoppingListNameControl.markAsUntouched();
      this.shoppingListNameControl.markAsPristine();
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['label']) {
      this.dataProductDetail = {
        ...ProductDetailData,
        content: this.label ?? '',
      };
    }

    if (changes['shoppingLists']) {
      this.initializeShoppingListOptions();
    }

    if (changes['defaultAmount']) {
      this.dataQuantityCounter = {
        ...ProductCounterData,
        value: this.defaultAmount,
      };

      this.quantityCounterControl.setValue(this.dataQuantityCounter.value);
    }
  }

  public onSubmit(): void {
    if (this.group.valid) {
      this.submitResult.emit(this.group.value as AddToShoppingListFormResult);
    }
  }

  private initializeShoppingListOptions(): void {
    let options: AutocompleteOptionModel[] = [];

    if (this.shoppingLists) {
      options = this.shoppingLists.map(
        (shoppingList: ShoppingListModel) => ({ label: shoppingList.name, value: shoppingList.id }),
      );
    }

    options.unshift({
      label: 'Neue Einkaufsliste',
      value: NewShoppingListId,
    });

    this.dataShoppingList = {
      ...ShoppingListInputData,
      options,
      value: options[1]?.value ?? options[0].value,
    };

    this.setNewListNameInputState(this.dataShoppingList.value as string);
  }

  private setNewListNameInputState(shoppingListValue: string): void {
    const shoppingListAttributes: AttributesModel =
      shoppingListValue === NewShoppingListId
        ? { required: true, disabled: false }
        : { required: false, disabled: true };

    this.dataShoppingListName = {
      ...this.dataShoppingListName,
      attributes: shoppingListAttributes,
    };

    // without this the control is enabled when the form is initially loaded
    this.changeDetector.detectChanges();
  }
}
